import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ApiError, toApiError } from "../api/client";

export interface QueryState<T> {
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
  /** Fetch again with the same key. */
  reload: () => void;
  /** Replace the cached data locally (e.g. after a successful mutation). */
  setData: (update: (prev: T | undefined) => T | undefined) => void;
}

interface Internal<T> {
  key: string | null;
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
}

/**
 * Minimal data-fetching hook. `key` identifies the request: whenever it changes the fetcher runs
 * again (pass `null` to skip). With `keepPrevious`, data for the previous key stays visible while
 * the next one loads (useful for filtered tables).
 */
export function useQuery<T>(
  key: string | null,
  fetcher: (signal: AbortSignal) => Promise<T>,
  options: { keepPrevious?: boolean } = {},
): QueryState<T> {
  const fetcherRef = useRef(fetcher);
  useLayoutEffect(() => {
    fetcherRef.current = fetcher;
  });
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<Internal<T>>({
    key: null,
    data: undefined,
    error: undefined,
    loading: key !== null,
  });

  useEffect(() => {
    if (key === null) return;
    const controller = new AbortController();
    let active = true;
    setState((s) => ({ ...s, loading: true, error: undefined }));
    fetcherRef.current(controller.signal).then(
      (data) => {
        if (active) setState({ key, data, error: undefined, loading: false });
      },
      (err: unknown) => {
        if (active) setState({ key, data: undefined, error: toApiError(err), loading: false });
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const setData = useCallback(
    (update: (prev: T | undefined) => T | undefined) =>
      setState((s) => ({ ...s, data: update(s.data) })),
    [],
  );

  const fresh = state.key === key;
  return {
    data: fresh || options.keepPrevious ? state.data : undefined,
    error: fresh ? state.error : undefined,
    loading: key !== null && (state.loading || !fresh),
    reload,
    setData,
  };
}
